/**
 * A salesperson's own numbers, cut three ways.
 *
 * Requested 3 Oct: "item category wise dashboard in place of carton" and
 * "dashboard in place of beat (customer wise and beat wise summary)".
 *
 * The day screen reports cartons and rupees, which answers "how much" and
 * nothing else. The three questions he is actually judged on are which
 * CATEGORIES he moved, which BEATS he covered and which CUSTOMERS bought —
 * and until now he could see none of them without an admin running a report.
 *
 * ONE FETCH, THREE VIEWS. All three come from the same orders, so fetching
 * them separately could show totals that disagree because an order landed
 * between two calls. The tabs switch what is displayed, not what is loaded.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Card, Empty, ErrorState, Money, Segment, Skeleton, TextInput, Tile,
} from '../../components/ui';
import { useQuery, useRefreshOnFocus } from '../../lib/useApi';
import { businessDate } from '../../lib/format';

type View = 'category' | 'beat' | 'customer';

interface Summary {
  from: string;
  to: string;
  byCategory: {
    id: string | null; name: string; quantity: number; valuePaise: number;
    display: { value: string };
  }[];
  byBeat: {
    id: string | null; name: string; orders: number; customers: number;
    valuePaise: number; display: { value: string };
  }[];
  byCustomer: {
    id: string; name: string; code: string; orders: number;
    valuePaise: number; outstandingPaise: number;
    display: { value: string; outstanding: string };
  }[];
}

export default function Summary() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('category');
  const [from, setFrom] = useState(() => businessDate());
  const [to, setTo] = useState(() => businessDate());

  const { data, loading, error, refetch } = useQuery<Summary>(
    `/api/field/my/summary?from=${from}&to=${to}`,
  );
  useRefreshOnFocus(refetch);

  const total = (data?.byCategory ?? []).reduce((n, r) => n + r.valuePaise, 0);
  const customers = (data?.byCustomer ?? []).length;

  return (
    <Page title="My summary">
      <div className="stack">
        <div className="row" style={{ gap: 8 }}>
          <TextInput label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <TextInput label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>

        {data && (
          <div className="row" style={{ gap: 8 }}>
            <Tile tone="green" label="Value" value={<Money paise={total} />} />
            <Tile tone="blue" label="Customers" value={String(customers)} />
          </div>
        )}

        <Segment<View>
          value={view}
          onChange={setView}
          options={[
            { value: 'category', label: 'Category' },
            { value: 'beat', label: 'Beat' },
            { value: 'customer', label: 'Customer' },
          ]}
        />

        {loading && !data && <Skeleton rows={4} />}
        {error && !data && <ErrorState message={error.message} onRetry={refetch} />}

        {/* --- By category -------------------------------------------------- */}
        {data && view === 'category' && (
          data.byCategory.length === 0
            ? <Empty title="No orders in this period" />
            : (
              <Card flush>
                <div className="list">
                  {data.byCategory.map((r) => (
                    <div key={r.id ?? 'none'} className="list__item list__item--static">
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="t-sm t-semi t-clip">{r.name}</div>
                        {/*
                          Base units. Five cartons and seven loose bottles is
                          127 — adding cartons to bottles is the arithmetic
                          this project treats as most dangerous, and a
                          dashboard is where nobody would check it.
                        */}
                        <div className="t-xs t-muted">{r.quantity} pcs</div>
                      </div>
                      <Money paise={r.valuePaise} className="t-sm" />
                    </div>
                  ))}
                </div>
              </Card>
            )
        )}

        {/* --- By beat ------------------------------------------------------ */}
        {data && view === 'beat' && (
          data.byBeat.length === 0
            ? <Empty title="No orders in this period" />
            : (
              <Card flush>
                <div className="list">
                  {data.byBeat.map((r) => (
                    <div key={r.id ?? 'none'} className="list__item list__item--static">
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="t-sm t-semi t-clip">{r.name}</div>
                        <div className="t-xs t-muted">
                          {r.orders} order(s) · {r.customers} customer(s)
                        </div>
                      </div>
                      <Money paise={r.valuePaise} className="t-sm" />
                    </div>
                  ))}
                </div>
              </Card>
            )
        )}

        {/* --- By customer -------------------------------------------------- */}
        {data && view === 'customer' && (
          data.byCustomer.length === 0
            ? <Empty title="No orders in this period" />
            : (
              <Card flush>
                <div className="list">
                  {data.byCustomer.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      className="list__item"
                      onClick={() => navigate(`/customers/${r.id}`)}
                    >
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="t-sm t-semi t-clip">{r.name}</div>
                        <div className="t-xs t-muted">
                          {r.code} · {r.orders} order(s)
                          {/*
                            What they still owe, next to what they bought.
                            A salesperson deciding who to call next needs both
                            numbers in the same row — the big buyer who has
                            not paid is the call worth making.
                          */}
                          {r.outstandingPaise > 0 && ` · owes ${r.display.outstanding}`}
                        </div>
                      </div>
                      <Money paise={r.valuePaise} className="t-sm" />
                    </button>
                  ))}
                </div>
              </Card>
            )
        )}
      </div>
    </Page>
  );
}
